import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Truck, ShieldCheck, RotateCcw, MessageCircle } from "lucide-react";
import { getProducts } from "@/lib/api";
import { IMAGES, STATS, REVIEWS, SERVICES } from "@/data/site";
import ProductCard from "@/components/ProductCard";
import Lookbook from "@/components/Lookbook";
import NewsletterSection from "@/components/NewsletterSection";
import useReveal from "@/hooks/useReveal";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";

const SERVICE_ICONS = [Truck, ShieldCheck, RotateCcw, MessageCircle];

const MARQUEE_FR = ["Huile d'Argan", "Ghassoul de l'Atlas", "Rose de Damas", "Fleur d'Oranger", "Nigelle", "Safran de Taliouine"];
const MARQUEE_NL = ["Arganolie", "Ghassoul uit de Atlas", "Damastroos", "Oranjebloesem", "Nigella", "Saffraan uit Taliouine"];

const WordReveal = ({ text }) => (
  <h1 data-testid="hero-title" aria-label={text}>
    {text.split(" ").map((w, i) => (
      <span className="wm" key={`${w}-${i}`} aria-hidden="true">
        <span className="wm-in" style={{ animationDelay: `${0.45 + i * 0.09}s` }}>{w}</span>
      </span>
    ))}
  </h1>
);

export default function Home() {
  const { t, lang } = useLang();
  const settings = useSettings();
  const [products, setProducts] = useState([]);
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, reduce ? 0 : 130]);
  useReveal([products.length]);

  useEffect(() => {
    getProducts()
      .then((data) => {
        const inStock = data.filter((p) => !(typeof p.stock === "number" && p.stock <= 0));
        const featured = (inStock.length >= 4 ? inStock : data).slice(0, 4);
        setProducts(featured);
      })
      .catch(() => {});
  }, []);

  const nl = lang === "nl";
  const heroEyebrow = (nl ? settings.hero_eyebrow_nl : settings.hero_eyebrow) || t("hero.eyebrow");
  const heroTitle = (nl ? settings.hero_title_nl : settings.hero_title) || t("hero.title");
  const heroSubtitle = (nl ? settings.hero_subtitle_nl : settings.hero_subtitle) || t("hero.subtitle");
  const marquee = nl ? MARQUEE_NL : MARQUEE_FR;

  return (
    <>
      {/* Hero plein écran */}
      <section className="hero-full" aria-label="Bannière principale" data-testid="home-hero">
        <motion.div className="hero-full__bgwrap" style={{ y: yBg }}>
          <img className="hero-full__bg" src={IMAGES.heroHome} alt="Paysage marocain baigné de lumière dorée" />
        </motion.div>
        <div className="hero-full__content">
          <span className="hero-line hero-anim" style={{ animationDelay: "0.15s" }} aria-hidden="true" />
          <p className="hero-full__eyebrow hero-anim" style={{ animationDelay: "0.3s" }} data-testid="hero-eyebrow">{heroEyebrow}</p>
          <WordReveal text={heroTitle} />
          <p className="sub hero-anim" style={{ animationDelay: "1.15s" }} data-testid="hero-subtitle">{heroSubtitle}</p>
          <span className="hero-anim" style={{ animationDelay: "1.35s", display: "inline-block" }}>
            <Link to="/collections/all" className="btn-hero" data-testid="hero-shop-btn">
              {t("common.discover")}
            </Link>
          </span>
        </div>
        <div className="hero-scrollcue" aria-hidden="true"><span /></div>
      </section>

      {/* Produits — collection mise en avant, juste sous le hero */}
      <section className="products-section products-section--lead reveal" aria-label="Notre collection">
        <div className="section-header stagger">
          <p className="section-tag">{t("home.signatureTag")}</p>
          <h2 className="section-title">{t("home.signatureTitle")}</h2>
          <p className="section-body" style={{ maxWidth: 520, margin: "0 auto" }}>
            {t("home.signatureDesc")}
          </p>
        </div>

        <div className="products-grid stagger" data-testid="home-products-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <Link to="/collections/all" className="btn-ghost" data-testid="home-view-all">{t("common.viewAll")}</Link>
        </div>
      </section>

      {/* Bandeau défilant ingrédients */}
      <div className="marquee" aria-hidden="true" data-testid="home-marquee">
        <div className="marquee__track">
          {[...marquee, ...marquee].map((m, i) => (
            <span key={i}>{m}<i>✦</i></span>
          ))}
        </div>
      </div>

      {/* Héritage */}
      <section className="heritage reveal" aria-label="Notre héritage">
        <div className="heritage-images">
          <div className="heritage-img img-curtain"><img src={IMAGES.heritage1} alt="Récolte manuelle à l'aube" loading="lazy" /></div>
          <div className="heritage-img img-curtain" style={{ transitionDelay: "0.15s" }}><img src={IMAGES.heritage2} alt="Montagnes de l'Atlas" loading="lazy" /></div>
        </div>
        <div className="heritage-text stagger">
          <p className="section-tag">{t("home.heritageTag")}</p>
          <h2 className="section-title">Le Respect de la Terre,<br /><em>le Secret de votre Éclat.</em></h2>
          <p className="section-body">
            Chaque ingrédient est issu de terres marocaines préservées, sélectionné selon des critères
            de pureté absolue. Nous collaborons directement avec les coopératives de femmes de l'Atlas
            pour garantir une traçabilité totale et un impact social positif.
          </p>
          <ul className="values-list">
            <li>Récolte manuelle à l'aube, au pic de concentration des actifs</li>
            <li>Extraction à froid, sans solvants ni procédés agressifs</li>
            <li>Soutien aux femmes artisanes de l'Atlas depuis 2019</li>
            <li>Formules sans perturbateurs endocriniens, sans parabènes</li>
            <li>Emballages 100% recyclables ou rechargeables</li>
          </ul>
        </div>
      </section>

      {/* Stats */}
      <div className="stats reveal stagger" role="region" aria-label="Nos chiffres clés">
        {STATS.map((s) => (
          <div className="stat-item" key={s.label}>
            <span className="stat-number">{s.number}</span>
            <span className="stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Lookbook Rituel Hammam */}
      <Lookbook />

      {/* Nature */}
      <section className="nature-section reveal" aria-label="La nature marocaine">
        <div className="nature-image img-curtain">
          <img src={IMAGES.nature} alt="Vallée des Roses, Maroc" loading="lazy" />
        </div>
        <div className="nature-text stagger">
          <p className="section-tag">{t("home.natureTag")}</p>
          <h2 className="section-title">Une beauté<br /><em>qui ne triche pas.</em></h2>
          <blockquote>« La nature ne triche pas. Elle soigne sans dissimuler, elle embellit sans artifices. »</blockquote>
          <p className="section-body">
            Au cœur du Maroc, des montagnes de l'Atlas aux vallées du Sud, chaque plante raconte une
            histoire d'équilibre et de transmission. Argan, rhassoul, fleur d'oranger, nigelle, rose de
            Damas — autant d'ingrédients récoltés avec respect, travaillés avec rigueur.
          </p>
          <Link to="/pages/notre-ethique" className="btn-primary" style={{ alignSelf: "flex-start" }} data-testid="nature-ethique-btn">
            {t("common.learnMore")}
          </Link>
        </div>
      </section>

      {/* Avis */}
      <section className="reviews-section reveal" aria-label="Avis clients">
        <p className="section-tag">{t("home.reviewsTag")}</p>
        <h2 className="section-title">{t("home.reviewsTitle")}</h2>
        <div className="reviews-grid stagger">
          {REVIEWS.map((r) => (
            <article className="review-card" key={r.author}>
              <div className="review-stars" aria-label="5 étoiles sur 5">★★★★★</div>
              <p className="review-text">« {r.text} »</p>
              <p className="review-author"><strong>{r.author}</strong> · {r.place}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Services */}
      <div className="shipping-section reveal stagger" role="region" aria-label="Nos services">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[i];
          return (
            <div className="shipping-item" key={s.title}>
              <div className="shipping-icon" aria-hidden="true"><Icon size={16} color="var(--gold)" strokeWidth={1.5} /></div>
              <p className="shipping-title">{s.title}</p>
              <p className="shipping-desc">{s.desc}</p>
            </div>
          );
        })}
      </div>

      <NewsletterSection />
    </>
  );
}
